import type { FC } from "react";
import { useRef } from "react";

import { StoreLocatorCardList } from "@locators/components/shared/components/StoreLocatorCardList";
import { StoreLocatorMap } from "@locators/components/shared/components/StoreLocatorMap";
import { StoreLocatorProviderWrapper } from "@locators/components/shared/components/StoreLocatorProviderWrapper";
import { StoreLocatorSearchBarV2 } from "@locators/components/shared/components/StoreLocatorSearchBarV2";
import { ViewSwitch } from "@locators/components/shared/components/ViewSwitch";
import { StoreLocatorContext } from "@locators/components/shared/context";
import { useCSSProperty, useHeight } from "@locators/components/shared/hooks";
import { useDisplayType } from "@locators/components/shared/hooks/useDisplayType";
import classNames from "classnames";

import { useDetectTheme } from "@jti/ui/hooks/useDetectTheme";

import { useHoReCaLocator } from "./hooks";
import type { HoReCaLocatorProps } from "./types";

import styles from "@locators/components/shared/styles/LocatorLayout.module.scss";

export const HoReCaLocator: FC<HoReCaLocatorProps> = (props) => {
  const { isDarkTheme } = useDetectTheme();

  const resolvedTheme = isDarkTheme ? ("dark" as const) : ("light" as const);

  const horecaLocatorData = useHoReCaLocator(props, resolvedTheme);

  const { isMapActive } = horecaLocatorData;
  const { change, isList, isMap } = useDisplayType("map");
  const { ref: topBarRef, height } = useHeight<HTMLDivElement>();
  const storeRef = useCSSProperty("--storeLocatorTopBarHeight", isMapActive ? `-${height}px` : "unset", [
    height,
    isMapActive,
  ]);
  const mainRef = useRef<HTMLDivElement>(null);

  return (
    <StoreLocatorProviderWrapper>
      <StoreLocatorContext.Provider value={horecaLocatorData}>
        <div
          className={classNames(styles.storeLocator, { [styles.storeLocatorFixedHeight]: isMap })}
          ref={storeRef}
          translate="no"
        >
          <div className={classNames(styles.top, { [styles.hide]: isMapActive && isMap })} ref={topBarRef}>
            <div className={classNames(styles.wrapper)}>
              <StoreLocatorSearchBarV2 isSearchInputFullWidth />
            </div>
          </div>

          <div className={classNames(styles.main)} ref={mainRef}>
            <div className={classNames(styles.wrapper, styles.desktopHidden)}>
              <ViewSwitch
                className={classNames(styles.viewSwitch, {
                  [styles.viewSwitchOnMap]: isMap,
                  [styles.fullSize]: isMapActive && isMap,
                })}
                onChangeResultType={change}
              />
            </div>
            <div className={classNames(styles.wrapper, styles.grid)}>
              <div
                className={classNames({
                  [styles.mobileHidden]: !isList,
                })}
              >
                <StoreLocatorCardList isMapView={isMap} mapWrapperRef={mainRef} />
              </div>
              <StoreLocatorMap className={classNames(styles.map, { [styles.mobileHidden]: !isMap })} />
            </div>
          </div>
        </div>
      </StoreLocatorContext.Provider>
    </StoreLocatorProviderWrapper>
  );
};
